import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'
import { eq } from 'ember-truth-helpers'
import { fn } from '@ember/helper'
import type { SortingBlock } from 'teacher-ui-client/components/data-operations/table'
import { includes } from 'teacher-ui-client/helpers/includes'
import { t, formatDate } from 'ember-intl'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import perform from 'ember-concurrency/helpers/perform'
import { array } from '@ember/helper'
import type { Task } from 'ember-concurrency'
import type { CertificateRow } from 'teacher-ui-client/manage/certificates/controller'
import type { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import type { Column } from 'teacher-ui-client/manage/certificates/controller'
import type { TOC } from '@ember/component/template-only'
import { get } from '@ember/helper'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { isDate } from 'lodash'

type PrintCertificatesTask = Task<void, [CertificateRow[]]>

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    themeKey?: ThemeKeys
    isAllSelected: boolean
    onUpdateSorting: (sortCompare: SortType, sortCol: string) => void
    onSelectAll: () => void
    sorting: SortingBlock<CertificateRow>
    columns: Column[]
    certificates: CertificateRow[]
    onSelectId: (id: string) => void
    selectedIds?: string[]
    printCertificates: PrintCertificatesTask
  }
}

interface ColumnWithDefinedSortableAndValuePath extends Column {
  sortable: SortType
  valuePath: string
}

function isColSortableAndValuePathDefined(col: Column): col is ColumnWithDefinedSortableAndValuePath {
  return Boolean(col.sortable && col.valuePath)
}

export const ManageCertificatesTable: TOC<Signature> = <template>
  <table ...attributes>
    <thead>
      <tr
        class="divide-x divide-white border-b border-white
          {{if (eq @themeKey 'blue') 'bg-oceany-blue-100'}}
          {{if (eq @themeKey 'green') 'bg-ms-green-100'}}"
      >
        {{#each @columns as |col|}}
          {{#if col.selectable}}
            <th class="w-0 text-center">
              <label class="cursor-pointer px-4 py-2">
                {{col.label}}
                <input type="checkbox" class="cursor-pointer" checked={{@isAllSelected}} {{on "change" @onSelectAll}} />
              </label>
            </th>
          {{else if (isColSortableAndValuePathDefined col)}}
            <th
              data-test-col={{col.valuePath}}
              class="px-4 py-2"
              role="button"
              {{on "click" (fn @onUpdateSorting col.sortable col.valuePath)}}
            >
              {{col.label}}
              <PhosphorIcon
                @icon={{if
                  (eq @sorting.sortCol col.valuePath)
                  (if (eq @sorting.sortDir "asc") "phosphor-caret-up-fill" "phosphor-caret-down-fill")
                  "phosphor-caret-up-down-fill"
                }}
                role="presentation"
              />
            </th>

          {{else}}
            <th class="px-4 py-2">{{col.label}}</th>
          {{/if}}
        {{/each}}
      </tr>
    </thead>
    <tbody class={{if @isReloading "opacity-50"}}>
      {{#each @certificates as |certificateRow index|}}
        <tr
          data-test-record-row={{index}}
          class="{{if (eq @themeKey 'blue') 'even:bg-oceany-blue-50 hover:bg-oceany-blue-100'}}
            {{if (eq @themeKey 'green') 'even:bg-ms-green-50 hover:bg-ms-green-100'}}"
        >
          {{#each @columns as |col|}}
            {{#if col.selectable}}
              <td class="w-0 text-center">
                <label class="cursor-pointer px-4 py-2" aria-label={{t "manage.certificates.selectRow"}}>
                  <input
                    type="checkbox"
                    class="cursor-pointer"
                    checked={{includes @selectedIds certificateRow.id}}
                    {{on "change" (fn @onSelectId certificateRow.id)}}
                  />
                </label>
              </td>
            {{else if col.valuePath}}
              {{#let (get certificateRow col.valuePath) as |val|}}
                {{#if col.formatAsDate}}
                  <td class="px-4 py-2" data-test-cell={{col.valuePath}}>
                    {{#if (isDate val)}}
                      {{formatDate val day="numeric" month="long" year="numeric"}}
                    {{else}}
                      {{t "na"}}
                    {{/if}}
                  </td>
                {{else}}
                  <td class="px-4 py-2" data-test-cell={{col.valuePath}}>
                    {{formatValue val}}
                  </td>
                {{/if}}
              {{/let}}
            {{else if col.isDownloadButton}}
              <td class="w-px px-4 py-2" data-test-cell={{col.valuePath}}>
                {{! (cloudflare certificate urls) }}
                <UiButtonLink
                  class="whitespace-nowrap"
                  @disabled={{@printCertificates.isRunning}}
                  {{on "click" (perform @printCertificates (array certificateRow))}}
                >
                  <PhosphorIcon @icon="phosphor-printer-fill" class="mr-2" />
                  {{t "manage.certificates.printButton"}}
                </UiButtonLink>
              </td>
            {{/if}}
          {{/each}}
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

export default ManageCertificatesTable
